import { useQuery } from '@tanstack/react-query'
import { useUser } from '@clerk/clerk-react'
import { format } from 'date-fns'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { BarChart3 } from 'lucide-react'
import { AnimatedLoader } from '@/components/ui/animated-loader'
import { searchService } from '@/lib/api/services'

export default function ClientSearchData() {
  const { user } = useUser()
  
  const { data: searchHistoryResponse, isLoading, error } = useQuery({
    queryKey: ['client-search-data'],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated')
      
      // Use the new API service
      return await searchService.getSearchHistory()
    },
    enabled: !!user?.id,
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Keep in cache for 10 minutes
    gcTime: 10 * 60 * 1000,
  })

  const searches = searchHistoryResponse?.searches || []

  const formatHourlyRate = (min: number | null, max: number | null) => {
    if (min === null && max === null) return 'Any'
    if (min === null) return `Up to $${max}`
    if (max === null) return `$${min}+`
    return `$${min} - $${max}`
  }

  const formatSearchTime = (timestamp: string) => {
    try {
      return format(new Date(timestamp), 'MMM d h:mmaaa')
    } catch {
      return 'Unknown'
    }
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Card>
          <CardContent className="flex items-center justify-center p-16">
            <AnimatedLoader message="Loading search data..." />
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Card>
          <CardContent className="flex items-center justify-center p-8">
            <div className="text-destructive">Failed to load search data. Please try again later.</div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Client Search Data
          </CardTitle>
          <CardDescription>
            Recent searches made by clients looking for AI expertise
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Search Query</TableHead>
                  <TableHead>Search Time</TableHead>
                  <TableHead>Languages</TableHead>
                  <TableHead>Skills</TableHead>
                  <TableHead>Hourly Rate Range</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {searches?.map((search, index) => (
                  <TableRow key={index}>
                    <TableCell className="max-w-xs">
                      <span className="line-clamp-2">
                        {search.searchQuery}
                      </span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatSearchTime(search.searchTime)}
                    </TableCell>
                    <TableCell>
                      {search.languages && search.languages.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {search.languages.map((lang, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {lang}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Any</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {search.skills && search.skills.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {search.skills.map((skill, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Any</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatHourlyRate(search.minRate, search.maxRate)}
                    </TableCell>
                  </TableRow>
                ))}
                {(!searches || searches.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No search data available yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}